import type { EnemyCoreStats } from "./enemy-stat-profile";

export const ENEMY_INSTANCE_STAT_RNG_SCHEMA_VERSION = 1 as const;

/** ±fraction applied to each base stat roll (0.3 → 70%–130% of base). */
export const DEFAULT_ENEMY_INSTANCE_STAT_VARIANCE_FRACTION = 0.3;

const ENEMY_CORE_STAT_KEYS = [
	"attack",
	"defence",
	"hitpoints",
	"manaPoints",
	"accuracy",
	"evasion",
	"speed",
	"critical",
] as const;

type EnemyCoreStatKey = (typeof ENEMY_CORE_STAT_KEYS)[number];

/** FNV-1a 32-bit (matches level-up stat RNG style). */
function fnv1a32(input: string): number {
	let h = 0x811c9dc5;
	for (let i = 0; i < input.length; i++) {
		h ^= input.charCodeAt(i);
		h = Math.imul(h, 0x01000193);
	}
	return h >>> 0;
}

function xorshift32(seed: number): [number, number] {
	let x = seed >>> 0;
	x ^= x << 13;
	x ^= x >>> 17;
	x ^= x << 5;
	return [x >>> 0, x >>> 0];
}

function mixSeed(
	seedKey: string,
	rngSchemaVersion: number,
	statKey: EnemyCoreStatKey,
): number {
	const s = `sq:enemyStatRng:v${rngSchemaVersion}:${seedKey}:${statKey}`;
	return fnv1a32(s);
}

function randIntInclusive(seed: number, min: number, max: number): number {
	if (max < min) {
		return min;
	}
	const span = max - min + 1;
	const [, r] = xorshift32(seed);
	return min + (r % span);
}

/**
 * Deterministic per-stat roll: base ± (varianceFraction × base), minimum 1.
 * Mirrors completion-overlay spirit (HP/MP use wider absolute bands at scale).
 */
export function rollEnemyInstanceStatValue(params: {
	baseValue: number;
	seedKey: string;
	statKey: EnemyCoreStatKey;
	varianceFraction?: number;
	rngSchemaVersion?: number;
}): number {
	const base = Math.max(0, Math.floor(params.baseValue));
	if (base <= 0) {
		return 1;
	}
	const varianceFraction = Math.max(
		0,
		Math.min(
			1,
			params.varianceFraction ??
				DEFAULT_ENEMY_INSTANCE_STAT_VARIANCE_FRACTION,
		),
	);
	const rngSchemaVersion =
		params.rngSchemaVersion ?? ENEMY_INSTANCE_STAT_RNG_SCHEMA_VERSION;
	const seed = mixSeed(params.seedKey, rngSchemaVersion, params.statKey);
	const delta = Math.max(1, Math.round(base * varianceFraction));
	const rolled = base + randIntInclusive(seed, -delta, delta);
	return Math.max(1, rolled);
}

export interface RollEnemyInstanceCoreStatsParams {
	baseStats: EnemyCoreStats;
	/** Stable encounter key (e.g. `buildEncounterSeedKey` output). */
	seedKey: string;
	varianceFraction?: number;
	rngSchemaVersion?: number;
}

export function rollEnemyInstanceCoreStats(
	params: RollEnemyInstanceCoreStatsParams,
): EnemyCoreStats {
	const { baseStats, seedKey } = params;
	const roll = (statKey: EnemyCoreStatKey, baseValue: number) =>
		rollEnemyInstanceStatValue({
			baseValue,
			seedKey,
			statKey,
			varianceFraction: params.varianceFraction,
			rngSchemaVersion: params.rngSchemaVersion,
		});
	return {
		attack: roll("attack", baseStats.attack),
		defence: roll("defence", baseStats.defence),
		hitpoints: roll("hitpoints", baseStats.hitpoints),
		manaPoints: roll("manaPoints", baseStats.manaPoints),
		accuracy: roll("accuracy", baseStats.accuracy),
		evasion: roll("evasion", baseStats.evasion),
		speed: roll("speed", baseStats.speed),
		critical: roll("critical", baseStats.critical),
	};
}
